import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { api } from '../utils/api'; // Import your API utility
import { getPlatformOverview } from '../utils/superadminApi';
import { getUnreadCount } from '../utils/feedbackApi';
import UserList from '../components/admin/UserList'; // Import the UserList component
import SessionList from '../components/admin/SessionList'; // Import the SessionList component
import OrganizationsList from '../components/admin/OrganizationsList';
import FeedbackManagement from '../components/admin/FeedbackManagement';

const StatCard = ({ title, value }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h4" component="p">
        {value}
      </Typography>
    </Paper>
  </Grid>
);

const OverviewTab = () => {
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // /stats is kept intact for the original dashboard cards; /overview adds
        // the new cross-org platform + growth metrics.
        const [statsRes, overviewRes] = await Promise.all([
          api.get('/admin/stats'),
          getPlatformOverview(),
        ]);
        setStats(statsRes.data);
        setOverview(overviewRes);
      } catch (err) {
        console.error('Error fetching admin overview:', err);
        setError(err.response?.data?.message || 'Failed to load statistics.');
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
  }

  return (
    <Box>
      {stats && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Application Statistics
          </Typography>
          <Grid container spacing={3}>
            <StatCard title="Total Users" value={stats.totalUsers} />
            <StatCard title="Total Sessions" value={stats.totalSessions} />
            <StatCard title="Active Sessions" value={stats.activeSessions} />
            <StatCard title="Total Items/Votes" value={stats.totalVotes} />
          </Grid>
        </Paper>
      )}

      {overview && (
        <>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Platform Growth
            </Typography>
            <Grid container spacing={3}>
              <StatCard title="Total Orgs" value={overview.totalOrgs} />
              <StatCard title="New Orgs (7d)" value={overview.orgsLast7} />
              <StatCard title="New Orgs (30d)" value={overview.orgsLast30} />
              <StatCard title="Total Teams" value={overview.totalTeams} />
              <StatCard title="Total Users" value={overview.totalUsers} />
              <StatCard title="Active Users (7d)" value={overview.activeUsers7} />
              <StatCard title="Active Users (30d)" value={overview.activeUsers30} />
              <StatCard title="Locked Orgs" value={overview.lockedOrgs} />
            </Grid>
          </Paper>

          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Plans &amp; Integrations
            </Typography>
            <Grid container spacing={3}>
              <StatCard title="Free" value={overview.planDistribution.free} />
              <StatCard title="Pro" value={overview.planDistribution.pro} />
              <StatCard title="Enterprise" value={overview.planDistribution.enterprise} />
              <StatCard title="Slack" value={overview.integrations.slack} />
              <StatCard title="GitHub" value={overview.integrations.github} />
              <StatCard title="SSO" value={overview.integrations.sso} />
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const data = await getUnreadCount();
        setUnreadFeedbackCount(data.count);
      } catch (err) {
        console.error('Error fetching unread feedback count:', err);
      }
    };
    fetchUnreadCount();
  }, [activeTab]); // Refetch when tab changes (in case feedback was reviewed)

  const tabs = [
    { value: 'overview', label: 'Overview' },
    { value: 'organizations', label: 'Organizations' },
    { value: 'users', label: 'Users' },
    { value: 'sessions', label: 'Sessions' },
    { value: 'feedback', label: 'Feedback' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Admin Dashboard
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={
              t.value === 'feedback' && unreadFeedbackCount > 0 ? (
                <Badge badgeContent={unreadFeedbackCount} color="warning" max={99}>
                  {t.label}
                </Badge>
              ) : (
                t.label
              )
            }
          />
        ))}
      </Tabs>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'organizations' && <OrganizationsList />}
      {activeTab === 'users' && <UserList />}
      {activeTab === 'sessions' && <SessionList />}
      {activeTab === 'feedback' && <FeedbackManagement />}
    </Box>
  );
};

export default AdminDashboardPage;
